import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Breadcrumb } from '../../../data/cache/database-cache.service';
import { BaseViewModel, BaseViewModelState } from '../../../shared/view-model/base-view-model';
import { KT_CHART_DISPLAY_ITEMS } from './user-summary-chart.config';
import { USER_SUMMARY_I18N } from './user-summary.i18n';
import {
  computeChartValues,
  computeUserDataSelections,
  createInitialChartValues,
} from './user-summary.helpers';
import { UserSummaryDataService } from './user-summary-data.service';
import { RecentUsersService } from '@app/core/layout/recent-users.service';

export interface UserSummaryState extends BaseViewModelState {
  breadcrumbs: Breadcrumb[];
  selectedUserWithUserData: any;
  selectedSectionGroupUserDataAttributes: any[];
  selectedSectionGroupFunctions: string[];
  userDataList: any[];
  userDataId0: string | null;
  userDataDate0: string | null;
  userDataId1: string | null;
  userDataDate1: string | null;
  uploadAsOfDate: string | null;
  isAnonymous: boolean;
  chartValues: number[];
}

const initialState: UserSummaryState = {
  status: 'idle',
  errorMessage: null,
  breadcrumbs: [],
  selectedUserWithUserData: null,
  selectedSectionGroupUserDataAttributes: [],
  selectedSectionGroupFunctions: [],
  userDataList: [],
  userDataId0: null,
  userDataDate0: null,
  userDataId1: null,
  userDataDate1: null,
  uploadAsOfDate: null,
  isAnonymous: false,
  chartValues: createInitialChartValues(),
};

@Injectable()
export class UserSummaryViewModel extends BaseViewModel<UserSummaryState> {
  private readonly destroyRef = inject(DestroyRef);
  private readonly dataService = inject(UserSummaryDataService);
  private readonly recentUsersService = inject(RecentUsersService);

  private userId: string | null = null;
  private parametersSubscriptionRegistered = false;
  private hasAddedToRecentUsers = false;
  private pendingPreferredDate: string | null = null;

  constructor() {
    super(initialState);
    this.registerParametersSubscription();
  }

  init(userId: string | null): void {
    if (!userId) {
      return;
    }
    if (this.userId === userId && this.currentState.status !== 'idle') {
      return;
    }

    this.userId = userId;
    this.hasAddedToRecentUsers = false;
    this.setLoading();
    this.dataService.setUserContext(userId);
    this.refreshFromCache();
  }

  selectAsOfDate(date: string | null): void {
    if (!date) {
      return;
    }
    const normalized = date.replace(/\//g, '-');
    this.pendingPreferredDate = normalized;
    const updated = this.computeUserDataSelections(this.currentState.userDataList, normalized);
    const chartValues = this.computeChartValues(
      this.currentState.userDataList,
      updated.primaryId,
    );
    this.patchState({
      userDataId0: updated.primaryId,
      userDataDate0: updated.primaryDate,
      userDataId1: updated.compareId,
      userDataDate1: updated.compareDate,
      uploadAsOfDate: updated.primaryDate,
      chartValues,
    });
  }

  private registerParametersSubscription(): void {
    if (this.parametersSubscriptionRegistered) {
      return;
    }
    this.parametersSubscriptionRegistered = true;
    this.dataService.parametersUpdated$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.refreshFromCache();
      });
  }

  private refreshFromCache(): void {
    if (!this.userId) {
      return;
    }

    const snapshot = this.dataService.getSnapshot();
    const selectedSection = snapshot.selectedSection;
    const breadcrumbs = selectedSection
      ? [
          { label: '', icon: 'home', link: ['/home', selectedSection.sectionId] },
          { label: USER_SUMMARY_I18N.breadcrumb.userList, icon: '', link: ['/home', selectedSection.sectionId, 'users'] },
          { label: USER_SUMMARY_I18N.breadcrumb.summary },
        ]
      : [];

    if (snapshot.userDataList == null) {
      this.patchState({
        status: 'loading',
        breadcrumbs,
        selectedUserWithUserData: snapshot.selectedUserWithUserData,
        selectedSectionGroupUserDataAttributes: snapshot.attributes ?? [],
        selectedSectionGroupFunctions: snapshot.functions,
        isAnonymous: this.userId === 'ANONYMOUS',
      });
      return;
    }

    const preferredDate = this.pendingPreferredDate;
    this.pendingPreferredDate = null;
    const selection = computeUserDataSelections(snapshot.userDataList, preferredDate);
    const chartValues = computeChartValues(snapshot.userDataList, selection.primaryId);

    this.patchState({
      status: 'success',
      breadcrumbs,
      selectedUserWithUserData: snapshot.selectedUserWithUserData,
      selectedSectionGroupUserDataAttributes: snapshot.attributes ?? [],
      selectedSectionGroupFunctions: snapshot.functions,
      userDataList: snapshot.userDataList ?? [],
      userDataId0: selection.primaryId,
      userDataDate0: selection.primaryDate,
      userDataId1: selection.compareId,
      userDataDate1: selection.compareDate,
      uploadAsOfDate: selection.primaryDate,
      isAnonymous: this.userId === 'ANONYMOUS',
      chartValues,
    });

    // Add to recent users (once per init)
    if (!this.hasAddedToRecentUsers && this.userId && this.userId !== 'ANONYMOUS') {
      const user = snapshot.selectedUserWithUserData;
      if (user && selectedSection) {
        // selectedUserWithUserData has nested structure: user.user.userName
        const userEntity = user?.user;
        const displayName =
          userEntity?.userName ||
          userEntity?.code ||
          [userEntity?.lastName, userEntity?.firstName].filter(Boolean).join(' ') ||
          this.userId;

        this.recentUsersService.addRecentUser({
          id: this.userId,
          name: displayName,
          sectionId: selectedSection.sectionId,
        });
        this.hasAddedToRecentUsers = true;
      }
    }
  }

  private computeUserDataSelections(list: any[], preferredDate?: string | null) {
    return computeUserDataSelections(list, preferredDate);
  }

  private computeChartValues(list: any[], primaryId: string | null) {
    return computeChartValues(list, primaryId);
  }
}
